import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { errorText } from '../../../lib/error-text';
import { programEnrollmentApi } from '../../../lib/api/program-enrollment.api';

// ─── Egresar de un programa ─────────────────────────────────────────────────
//
// GRADUAR NO SE DESHACE: la entidad no tiene inversa de `markGraduated` y
// `withdraw` rechaza una inscripción graduada. Por eso pasa por confirmación y
// la confirmación nombra el programa: la persona puede tener varios, y
// equivocarse de programa no tiene vuelta atrás.
//
// SE PUEDE EGRESAR SIN COMPLETAR EL PLAN —un centro que carga su historia lo
// necesita—, pero entonces se exige un motivo y escribir el nombre del
// programa. El servidor mide la completitud por su cuenta.

export function GraduateProgramDialog({
  open,
  onClose,
  onDone,
  tenantId,
  personId,
  programEnrollmentId,
  programId,
  programName,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => Promise<void> | void;
  tenantId: string;
  personId: string;
  programEnrollmentId: string;
  programId: string;
  programName: string;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');

  // EL DATO ANTES DE LA DECISIÓN: quien aprieta tiene que ver de qué tamaño es
  // el hueco. La misma consulta que la pestaña de avance.
  const auditQ = useQuery({
    queryKey: ['person-degree-audit', tenantId, personId, programId],
    enabled: open,
    queryFn: () =>
      programEnrollmentApi.getDegreeAudit({ tenantId, studentId: personId, programId }),
    staleTime: 60_000,
  });
  const audit = auditQ.data;
  const incomplete =
    !!audit && (audit.pending.length > 0 || audit.creditsEarned < audit.creditsRequired);

  // LA CONFIRMACIÓN ESCRITA PIDE EL PROGRAMA, NO A LA PERSONA: lo que está en
  // juego es egresar del programa equivocado.
  const typedMatches = typed.trim().toLocaleLowerCase() === programName.trim().toLocaleLowerCase();
  const canGraduate = !!audit && (!incomplete || (reason.trim().length > 0 && typedMatches));

  const close = () => {
    setError(null);
    setReason('');
    setTyped('');
    onClose();
  };

  const graduate = async () => {
    setBusy(true);
    setError(null);
    try {
      await programEnrollmentApi.markGraduated({
        tenantId,
        programEnrollmentId,
        ...(incomplete ? { incompleteReason: reason.trim() } : {}),
      });
      await onDone();
      close();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle>{t('career.graduateDialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('career.graduateDialog.body', { program: programName })}
        </DialogContentText>
        <DialogContentText sx={{ mt: 2 }}>
          {t('career.graduateDialog.irreversible')}
        </DialogContentText>
        {auditQ.isLoading && <CircularProgress size={20} sx={{ mt: 2 }} />}
        {incomplete && audit && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              {t('career.graduateDialog.incompleteTitle')}
            </Typography>
            <Typography variant="body2">
              {t('career.graduateDialog.incompleteDetail', {
                earned: audit.creditsEarned,
                required: audit.creditsRequired,
                count: audit.pending.length,
              })}
            </Typography>
          </Alert>
        )}
        {incomplete && (
          <>
            <TextField
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              label={t('career.graduateDialog.reasonLabel')}
              helperText={t('career.graduateDialog.reasonHelp')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label={t('career.graduateDialog.typeProgramLabel')}
              helperText={t('career.graduateDialog.typeProgramHelp', { program: programName })}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              error={typed.length > 0 && !typedMatches}
              disabled={busy}
            />
          </>
        )}
        {(error || auditQ.isError) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error ?? errorText(auditQ.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="info"
          onClick={() => void graduate()}
          disabled={busy || !canGraduate}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {t('career.actions.graduate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
